import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import { Button, FormGroup } from "@cofano/cck";
import { Box, CircularProgress } from "@mui/material";

import { useConfirm } from "components/hooks/useConfirm";

import { DataFormat, Language } from "imports/Utils";

import { LocationLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import CraneSpeedDialog from "view/availability/components/dialogs/CraneSpeedDialog";
import AvailabilityCreateEditDialog from "view/availability/components/dialogs/AvailabilityCreateEditDialog";

import { CraneType, LocationAvailabilityType } from "__generated__/enums";
import { CompactLocationFragment } from "__generated__/view/locations/graphql/location.fragments.generated";
import { GetLocationAvailabilityDocument } from "__generated__/view/availability/graphql/quayAvailability.queries.generated";
import { DeleteLocationAvailabilityDocument } from "__generated__/view/availability/graphql/quayAvailability.mutations.generated";

interface AvailabilitySideviewerProps {
	location: Pick<CompactLocationFragment, "id" | "description">
	availabilityId: number;
	onUpdate: () => void;
	onDelete: () => void;
}

const AvailabilitySideviewer = ({ location, availabilityId, onUpdate, onDelete }: AvailabilitySideviewerProps) => {

	const confirm = useConfirm();

	const { data, loading, refetch } = useQuery(GetLocationAvailabilityDocument, {
		variables: { id: availabilityId },
	});

	const availability = data?.locationAvailability;

	const [ craneSpeedModalOpen, openCraneSpeedModal ] = useState(false);
	const [ availabilityDialogOpen, openAvailabilityDialog ] = useState(false);

	const isCraneType = availability?.type === LocationAvailabilityType.CraneAvailability;
	const isQuayType = availability?.type === LocationAvailabilityType.MooringAvailability;
	const isContainerCrane = availability?.crane?.craneType === CraneType.Container;
	const craneSpeedEnabled = isQuayType || (isCraneType && !availability.crane) || (isContainerCrane);

	const [ deleteAvailability, { loading: deleteLoading } ] = useMutation(DeleteLocationAvailabilityDocument, {
		onCompleted: () => onDelete(),
	});

	if (loading || !availability) {
		return <CircularProgress size={24} />;
	} else {
		return (
			<div>
				<Box sx={{ display: "flex", justifyContent: "end", mb: 2, gap: 1 }}>
					<Button onClick={() => openAvailabilityDialog(true)} size="small">
						{Language("EDIT")}
					</Button>

					<Button
						onClick={() => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deleteAvailability({
									variables: {
										id: availability.id,
									},
								});
							});
						}}
						loading={deleteLoading}
						color="danger"
						size="small">
						{Language("DELETE")}
					</Button>
				</Box>

				<FormGroup label={Language("LOCATION")} labelPlacement="left-center" overflow="hidden">
					<LocationLink location={location} />
				</FormGroup>

				<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
					{availability.type === LocationAvailabilityType.CraneAvailability ? Language("MOORING_AVAILABILITIES_CRANE") : Language("MOORING")}
				</FormGroup>

				{isCraneType && (
					<FormGroup label={Language("MOORING_AVAILABILITIES_CRANE")} labelPlacement="left-center" overflow="hidden">
						{availability.crane?.name}
					</FormGroup>
				)}

				{isQuayType && (
					<FormGroup label={Language("MOORING_AVAILABILITIES_CRANES_AMOUNT")} labelPlacement="left-center" overflow="hidden">
						{availability.amountOfCranes}
					</FormGroup>
				)}

				<FormGroup label={Language("MOORING_AVAILABILITIES_CRANE_SPEED")} labelPlacement="left-center" overflow="hidden">
					{craneSpeedEnabled ? (
						<div>
							{DataFormat.simpleUnit(availability.craneSpeed || 0, DataFormat.UNITS.MOVH)}
						</div>
					) : (
						<div>
							<Button onClick={() => openCraneSpeedModal(true)} size="small" variant="outlined">
								{Language("DETAILS")}
							</Button>
						</div>
					)}
				</FormGroup>

				<FormGroup label={Language("MOORING_AVAILABILITIES_FROM")} labelPlacement="left-center" overflow="hidden">
					<FormattedDate date={availability.fromDateTime} />
				</FormGroup>

				<FormGroup label={Language("MOORING_AVAILABILITIES_TO")} labelPlacement="left-center" overflow="hidden">
					<FormattedDate date={availability.toDateTime} />
				</FormGroup>

				<FormGroup label={Language("MOORING_AVAILABILITIES_BOLDER_FROM")} labelPlacement="left-center" overflow="hidden">
					{availability.bolderFrom.description}
				</FormGroup>

				<FormGroup label={Language("MOORING_AVAILABILITIES_BOLDER_TO")} labelPlacement="left-center" overflow="hidden">
					{availability.bolderTo.description}
				</FormGroup>

				{(isQuayType || isContainerCrane) && (
					<React.Fragment>
						<FormGroup label={Language("MOORING_AVAILABILITIES_MAX_WIDTH")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.simpleUnit(availability.maxWidthInMeters || 0, DataFormat.UNITS.METER)}
						</FormGroup>

						<FormGroup label={Language("LOCATION_AVAILABILITIES_OVERFLOW_LOCATIONS")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.boolean(Array.isArray(availability.overflowLocations) && availability.overflowLocations.length > 0)}
						</FormGroup>

						<FormGroup label={Language("MOORING_AVAILABILITIES_CAN_HANDLE_OOG")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.boolean(availability.canHandleOOG)}
						</FormGroup>

						<FormGroup label={Language("MOORING_AVAILABILITIES_CAN_HANDLE_BUNDLES")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.boolean(availability.canHandleBundles)}
						</FormGroup>

						<FormGroup label={Language("MOORING_AVAILABILITIES_CAN_HANDLE_FULL_CONTAINERS")} labelPlacement="left-center" overflow="visible">
							{DataFormat.boolean(availability.fullContainersAllowed)}
						</FormGroup>

						<FormGroup label={Language("MOORING_AVAILABILITIES_TWINNING_ALLOWED")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.boolean(availability.twinningAllowed)}
						</FormGroup>
					</React.Fragment>
				)}

				<CraneSpeedDialog
					open={craneSpeedModalOpen}
					onClose={() => openCraneSpeedModal(false)}
					handlingRates={availability.crane?.handlingRates ?? []} />

				<AvailabilityCreateEditDialog
					open={availabilityDialogOpen}
					onClose={() => openAvailabilityDialog(false)}
					defaultLocation={location}
					availabilityId={availabilityId}
					refetch={() => {
						refetch();
						onUpdate();
					}}
					presetAvailabilityType={availability.type !== LocationAvailabilityType.TerminalTransferAvailability ? availability.type : undefined}
				/>
			</div>
		);
	}
};

export default AvailabilitySideviewer;
